import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pill,
  Clock,
  Stethoscope,
  Ban,
  ShieldAlert,
  Activity,
  Thermometer,
  Search,
  Baby,
  Users,
  ArrowLeft,
  Heart,
  AlertTriangle,
  Printer,
  Share2,
  Star,
  X,
  Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const gravidanzaStyle = (val = "") => {
  const v = val.toLowerCase();
  if (v.startsWith("s")) return "bg-teal-light text-teal-dark";
  if (v.startsWith("n")) return "bg-warn-light text-warn-dark";
  return "bg-amber-50 text-amber-700";
};

function Loader() {
  return (
    <div className="flex flex-col items-center justify-center py-24" data-testid="loading-indicator">
      <div className="relative flex items-center justify-center">
        <span className="absolute inline-flex h-20 w-20 rounded-full bg-brand/30 pulse-ring" />
        <span className="absolute inline-flex h-20 w-20 rounded-full bg-brand/20 pulse-ring" style={{ animationDelay: "0.6s" }} />
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand shadow-lg">
          <Pill className="h-8 w-8 text-white pill-wobble" />
        </div>
      </div>
      <p className="mt-8 text-sm font-medium tracking-wide text-slate-500">
        Consultazione del bugiardino in corso...
      </p>
    </div>
  );
}

function Card({ icon: Icon, title, color, children, span, index, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      className={`group rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${span ? "md:col-span-2" : ""}`}
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}1A` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      </div>
      <div className="text-[15px] leading-relaxed text-slate-600">{children}</div>
    </motion.div>
  );
}

function ListContent({ items, dotColor }) {
  if (!items || items.length === 0) return <span className="text-slate-400">Nessuna informazione disponibile.</span>;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Results({ data, onReset, onShare, isFav, onToggleFav }) {
  const p = data.precauzioni || {};
  const e = data.effetti_indesiderati || {};
  return (
    <div className="w-full" id="print-area">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex flex-col items-center text-center"
      >
        <span className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand">Risultati per</span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="result-medicine-name">
          {data.nome}
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card icon={Clock} title="POSOLOGIA" color="#2A7FD4" index={0} testid="result-card-posologia">
          {data.posologia}
        </Card>

        <Card icon={Stethoscope} title="CURA" color="#00C9A7" index={1} testid="result-card-cura">
          {data.cura}
        </Card>

        <Card icon={Ban} title="CONTROINDICAZIONI" color="#FF6B6B" index={2} testid="result-card-controindicazioni">
          <ListContent items={data.controindicazioni} dotColor="#FF6B6B" />
        </Card>

        <Card icon={ShieldAlert} title="PRECAUZIONI" color="#F59E0B" index={3} testid="result-card-precauzioni">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Heart className="h-4 w-4 text-pink-500" /> Gravidanza</span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${gravidanzaStyle(p.gravidanza)}`}>{p.gravidanza || "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Baby className="h-4 w-4 text-brand" /> Bambini</span>
              <span className="rounded-full bg-brand-light px-3 py-1 text-right text-xs font-semibold text-brand-hover">{p.bambini || "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Users className="h-4 w-4 text-slate-500" /> Anziani</span>
              <span className="rounded-full bg-slate-200/70 px-3 py-1 text-right text-xs font-semibold text-slate-600">{p.anziani || "—"}</span>
            </div>
          </div>
        </Card>

        <Card icon={Activity} title="EFFETTI INDESIDERATI" color="#8B5CF6" index={4} span testid="result-card-effetti">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Comuni</p>
              <ListContent items={e.comuni} dotColor="#8B5CF6" />
            </div>
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-warn-dark">
                <AlertTriangle className="h-3.5 w-3.5" /> Gravi
              </p>
              <ListContent items={e.gravi} dotColor="#FF6B6B" />
            </div>
          </div>
        </Card>

        <Card icon={Thermometer} title="CONSERVAZIONE & SCADENZA" color="#0EA5E9" index={5} span testid="result-card-conservazione">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Conservazione</p>
              <p>{data.conservazione}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Scadenza</p>
              <p>{data.scadenza}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3 no-print">
        <button
          onClick={onToggleFav}
          data-testid="favorite-toggle"
          className={`flex items-center gap-2 rounded-full border px-6 py-2.5 text-sm font-medium transition-all ${
            isFav
              ? "border-amber-300 bg-amber-50 text-amber-600"
              : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:text-amber-600"
          }`}
        >
          <Star className={`h-4 w-4 ${isFav ? "fill-amber-400 text-amber-400" : ""}`} />
          {isFav ? "Nei preferiti" : "Aggiungi ai preferiti"}
        </button>
        <button
          onClick={onShare}
          data-testid="share-button"
          className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-600 transition-all hover:border-brand hover:text-brand"
        >
          <Share2 className="h-4 w-4" /> Condividi
        </button>
        <button
          onClick={() => window.print()}
          data-testid="print-button"
          className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-600 transition-all hover:border-brand hover:text-brand"
        >
          <Printer className="h-4 w-4" /> Stampa / PDF
        </button>
        <button
          onClick={onReset}
          data-testid="reset-button"
          className="flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand-hover"
        >
          <ArrowLeft className="h-4 w-4" /> Cerca un altro farmaco
        </button>
      </div>
    </div>
  );
}

function NotFound({ onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mx-auto max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="error-message"
    >
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-warn-light">
        <Search className="h-7 w-7 text-warn" />
      </div>
      <h3 className="font-display text-xl font-bold text-slate-900">Farmaco non trovato</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Verifica il nome o consulta un farmacista.
      </p>
      <button
        onClick={onReset}
        data-testid="reset-button"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white transition-all hover:bg-brand-hover"
      >
        <ArrowLeft className="h-4 w-4" /> Riprova
      </button>
    </motion.div>
  );
}

function Home() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | results | notfound | error
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bugiardino_recent") || "[]");
    } catch (e) {
      return [];
    }
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bugiardino_favorites") || "[]");
    } catch (e) {
      return [];
    }
  });
  const inputRef = useRef(null);

  const persistFavorites = (list) => {
    setFavorites(list);
    localStorage.setItem("bugiardino_favorites", JSON.stringify(list));
  };

  const isFavorite = (name) => favorites.some((f) => f.toLowerCase() === (name || "").toLowerCase());

  const toggleFavorite = (name) => {
    if (!name) return;
    if (isFavorite(name)) {
      persistFavorites(favorites.filter((f) => f.toLowerCase() !== name.toLowerCase()));
      toast.success("Rimosso dai preferiti");
    } else {
      persistFavorites([name, ...favorites].slice(0, 20));
      toast.success("Aggiunto ai preferiti");
    }
  };

  const removeFavorite = (name) => {
    persistFavorites(favorites.filter((f) => f.toLowerCase() !== name.toLowerCase()));
  };

  const addRecent = (name) => {
    if (!name) return;
    setRecent((prev) => {
      const next = [name, ...prev.filter((r) => r.toLowerCase() !== name.toLowerCase())].slice(0, 6);
      localStorage.setItem("bugiardino_recent", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get("farmaco");
    if (param) {
      runSearch(param);
    } else {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 1 || status !== "idle") {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/suggest`, { params: { q: term } });
        setSuggestions(res.data?.suggestions || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query, status]);

  const runSearch = async (term) => {
    const name = (term ?? query).trim();
    if (!name) return;
    setShowSuggest(false);
    setSuggestions([]);
    setQuery(name);
    setStatus("loading");
    setData(null);
    try {
      const res = await axios.post(`${API}/search`, { name });
      if (res.data?.not_found) {
        setStatus("notfound");
      } else {
        setData(res.data);
        setStatus("results");
        const url = `${window.location.pathname}?farmaco=${encodeURIComponent(res.data.nome || name)}`;
        window.history.replaceState({}, "", url);
        addRecent(res.data.nome || name);
      }
    } catch (e) {
      setStatus("error");
    }
  };

  const reset = () => {
    setStatus("idle");
    setData(null);
    setQuery("");
    window.history.replaceState({}, "", window.location.pathname);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = data?.nome ? `BUGIARDINO — ${data.nome}` : "BUGIARDINO";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch (e) {
      /* user cancelled or unsupported, fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch (e) {
      toast.error("Impossibile copiare il link");
    }
  };

  const showHeroCentered = status === "idle";

  return (
    <div className="App flex min-h-screen flex-col bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-slate-100 bg-[#F8FAFC]/80 backdrop-blur-md no-print">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand shadow-sm">
              <Pill className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight text-slate-900" data-testid="app-title">
              BUGIARDINO
            </span>
          </Link>
          <Link
            to="/contatti"
            data-testid="contatti-link"
            className="text-sm font-medium text-slate-600 transition-colors hover:text-brand"
          >
            Contatti
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 sm:px-6 lg:px-8">
        <motion.div
          layout
          className={`flex flex-col items-center ${showHeroCentered ? "justify-center py-20 sm:py-28" : "pt-10 pb-6"}`}
        >
          <AnimatePresence>
            {showHeroCentered && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-8 text-center"
              >
                <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl">
                  Il foglietto illustrativo,<br />
                  <span className="text-brand">semplice e chiaro.</span>
                </h1>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-500">
                  Cerca un farmaco e leggi le informazioni essenziali del suo bugiardino.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
            className="relative w-full max-w-2xl no-print"
          >
            <div className="relative">
              <Pill className="pointer-events-none absolute left-5 top-1/2 h-6 w-6 -translate-y-1/2 text-brand" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSuggest(true);
                }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                autoComplete="off"
                placeholder="Scrivi il nome del farmaco..."
                className="h-16 w-full rounded-full border-2 border-slate-200 bg-white pl-14 pr-32 text-lg text-slate-900 shadow-lg outline-none transition-all placeholder:text-slate-400 focus:border-brand focus:ring-4 focus:ring-brand/20"
              />
              <button
                type="submit"
                data-testid="search-submit"
                className="absolute right-2.5 top-1/2 flex h-12 -translate-y-1/2 items-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-white transition-all hover:bg-brand-hover active:scale-95"
              >
                <Search className="h-4 w-4" /> Cerca
              </button>
            </div>

            <AnimatePresence>
              {showHeroCentered && showSuggest && suggestions.length > 0 && (
                <motion.ul
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  data-testid="suggestions-list"
                  className="absolute left-0 right-0 top-[4.75rem] z-30 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl"
                >
                  {suggestions.map((s) => (
                    <li key={s}>
                      <button
                        type="button"
                        data-testid="suggestion-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          runSearch(s);
                        }}
                        className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-[15px] text-slate-700 transition-colors hover:bg-brand-light hover:text-brand-hover"
                      >
                        <Search className="h-4 w-4 text-slate-300" />
                        {s}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </form>

          {/* Favorites */}
          {showHeroCentered && favorites.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="mt-6 flex w-full max-w-2xl flex-wrap items-center justify-center gap-2"
            >
              <span className="flex items-center gap-1 text-xs font-medium text-amber-500">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Preferiti:
              </span>
              {favorites.map((f) => (
                <span
                  key={f}
                  data-testid="favorite-item"
                  className="group/fav flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 py-1 pl-3 pr-1.5 text-xs font-medium text-amber-700 transition-all hover:border-amber-300"
                >
                  <button onClick={() => runSearch(f)} className="transition-colors hover:text-amber-900">
                    {f}
                  </button>
                  <button
                    data-testid="favorite-remove"
                    onClick={() => removeFavorite(f)}
                    aria-label={`Rimuovi ${f} dai preferiti`}
                    className="flex h-4 w-4 items-center justify-center rounded-full text-amber-400 transition-colors hover:bg-amber-200 hover:text-amber-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </motion.div>
          )}

          {/* Recent searches */}
          {showHeroCentered && recent.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-6 flex flex-wrap items-center justify-center gap-2"
            >
              <span className="text-xs font-medium text-slate-400">Le tue ricerche recenti:</span>
              {recent.map((r) => (
                <button
                  key={r}
                  data-testid="recent-search-item"
                  onClick={() => runSearch(r)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition-all hover:border-brand hover:text-brand"
                >
                  {r}
                </button>
              ))}
            </motion.div>
          )}
        </motion.div>

        {/* Body states */}
        <div className="pb-16" aria-live="polite">
          {status === "loading" && <Loader />}
          {status === "results" && data && (
            <Results
              data={data}
              onReset={reset}
              onShare={handleShare}
              isFav={isFavorite(data.nome)}
              onToggleFav={() => toggleFavorite(data.nome)}
            />
          )}
          {status === "notfound" && <NotFound onReset={reset} />}
          {status === "error" && (
            <div className="mx-auto max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center" data-testid="error-message">
              <p className="text-sm text-slate-600">
                Si è verificato un errore. Riprova tra qualche istante.
              </p>
              <button
                onClick={reset}
                data-testid="reset-button"
                className="mt-4 rounded-full bg-brand px-6 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
              >
                Riprova
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Disclaimer */}
      <footer className="border-t border-slate-200 bg-slate-100 py-4 no-print" data-testid="disclaimer">
        <p className="mx-auto max-w-3xl px-4 text-center text-xs leading-relaxed text-slate-500">
          BUGIARDINO è uno strumento informativo. Non sostituisce il consiglio del medico o del farmacista.
        </p>
        <p className="mt-2 px-4 text-center text-xs text-slate-400" data-testid="copyright">
          © 2026 www.bugiardino.net – Tutti i diritti riservati. Il design, il codice, i contenuti e il concept di questa piattaforma sono protetti dalle leggi sul diritto d'autore. È vietata la riproduzione, copia o imitazione, anche parziale, senza autorizzazione scritta del titolare.
        </p>
      </footer>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}

function Contatti() {
  return (
    <div className="App flex min-h-screen flex-col bg-[#F8FAFC]">
      <header className="sticky top-0 z-20 border-b border-slate-100 bg-[#F8FAFC]/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand shadow-sm">
              <Pill className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight text-slate-900">
              BUGIARDINO
            </span>
          </Link>
          <Link to="/contatti" className="text-sm font-medium text-brand">
            Contatti
          </Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center px-4 py-20 sm:px-6 lg:px-8">
        <div className="w-full max-w-lg rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-10" data-testid="contatti-page">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand-light">
            <Mail className="h-7 w-7 text-brand" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Contatti</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Per collaborazioni e pubblicità scrivere a:
          </p>
          <a
            href="mailto:info@bugiardino.net"
            data-testid="contact-email"
            className="mt-2 inline-block text-lg font-semibold text-brand transition-colors hover:text-brand-hover"
          >
            info@bugiardino.net
          </a>
          <div className="mt-8">
            <Link
              to="/"
              data-testid="back-home-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-600 transition-all hover:border-brand hover:text-brand"
            >
              <ArrowLeft className="h-4 w-4" /> Torna alla ricerca
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-slate-100 py-4">
        <p className="px-4 text-center text-xs text-slate-400">
          © 2026 www.bugiardino.net – Tutti i diritti riservati. Il design, il codice, i contenuti e il concept di questa piattaforma sono protetti dalle leggi sul diritto d'autore. È vietata la riproduzione, copia o imitazione, anche parziale, senza autorizzazione scritta del titolare.
        </p>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/contatti" element={<Contatti />} />
      </Routes>
    </BrowserRouter>
  );
}
